/**
 * =============================================================================
 * PageMoveHistoryDialog.tsx — Cronologia spostamenti pagine (ultimi 7 giorni)
 * =============================================================================
 *
 * Dialog che legge da `page_move_history` e mostra gli spostamenti effettuati
 * negli ultimi 7 giorni. Per ciascuna voce ancora annullabile (status =
 * 'executed') espone un pulsante "Annulla" che invoca l'RPC `undo_page_move`.
 *
 * Nota: il server espone fino a 90gg di storico, ma la finestra di undo
 * "esteso" concordata (Q2) è 7gg per evitare errori di ripensamento tardivo.
 * Le voci più vecchie restano visibili ma non annullabili dall'UI.
 * =============================================================================
 */
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Undo2, History as HistoryIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { withRpcRetry } from "@/lib/rpcRetry";

/** Voce di history (sottoinsieme dei campi della tabella). */
interface HistoryRow {
  id: string;
  page_id: string;
  page_title: string;
  source_section_title: string | null;
  dest_section_title: string | null;
  moved_count: number;
  status: "executed" | "undone" | "redone";
  user_email: string | null;
  created_at: string;
}

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Callback chiamata dopo un undo applicato con successo. */
  onUndoApplied?: () => void;
}

export function PageMoveHistoryDialog({ open, onOpenChange, onUndoApplied }: Props) {
  const [rows, setRows] = useState<HistoryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      // Recupera join "user_email" via tabella page_move_history (campo già
      // presente lato history ma non ancora salvato in colonna: la otteniamo
      // via la nuova logica del Blocco 2 che però la scrive in admin_audit_log,
      // non qui. Preserviamo "user_id" e ne ricaviamo email se possibile.).
      const { data, error } = await supabase
        .from("page_move_history")
        .select(
          "id, page_id, page_title, source_section_title, dest_section_title, moved_count, status, created_at, user_id",
        )
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      const mapped: HistoryRow[] = (data || []).map((r: any) => ({
        id: r.id,
        page_id: r.page_id,
        page_title: r.page_title,
        source_section_title: r.source_section_title,
        dest_section_title: r.dest_section_title,
        moved_count: r.moved_count,
        status: r.status,
        user_email: null, // riservato a estensione futura via join profiles
        created_at: r.created_at,
      }));
      setRows(mapped);
    } catch (e: any) {
      console.error("Errore caricamento cronologia spostamenti:", e);
      toast.error("Impossibile caricare la cronologia: " + (e?.message ?? "errore"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const handleUndo = async (row: HistoryRow) => {
    setBusyId(row.id);
    try {
      const { error } = await withRpcRetry<{ data: any; error: any }>(() =>
        supabase.rpc("undo_page_move", { p_history_id: row.id }) as any,
      );
      if (error) throw error;
      toast.success(`Spostamento di "${row.page_title}" annullato`);
      onUndoApplied?.();
      await load();
    } catch (e: any) {
      console.error("Errore undo da cronologia:", e);
      toast.error(e?.message || "Impossibile annullare lo spostamento");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HistoryIcon className="w-5 h-5" /> Cronologia spostamenti pagine
          </DialogTitle>
          <DialogDescription>
            Ultimi 50 spostamenti registrati. Quelli effettuati negli ultimi 7
            giorni e non ancora annullati sono ripristinabili con un click.
            Oltre i 7 giorni la voce è di sola consultazione (storico esteso
            disponibile lato server fino a 90 giorni).
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Carico la cronologia…
          </div>
        ) : rows.length === 0 ? (
          <div className="py-10 text-center text-muted-foreground text-sm">
            Nessuno spostamento registrato.
          </div>
        ) : (
          <ul className="divide-y border rounded-md">
            {rows.map((r) => {
              const ts = new Date(r.created_at).getTime();
              const ageMs = Date.now() - ts;
              const undoable = r.status === "executed" && ageMs <= SEVEN_DAYS_MS;
              return (
                <li key={r.id} className="p-3 flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{r.page_title}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Da <span className="font-mono">{r.source_section_title ?? "—"}</span>
                      {" → "}
                      <span className="font-mono">{r.dest_section_title ?? "—"}</span>
                      {r.moved_count > 1 && (
                        <span className="ml-2 inline-flex items-center rounded bg-muted px-1.5 py-0.5 text-[10px]">
                          {r.moved_count} nodi
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      {new Date(r.created_at).toLocaleString("it-IT")}
                      {r.status === "undone" && (
                        <span className="ml-2 text-amber-600">• già annullato</span>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant={undoable ? "default" : "ghost"}
                    disabled={!undoable || busyId === r.id}
                    onClick={() => handleUndo(r)}
                    title={
                      undoable
                        ? "Annulla questo spostamento"
                        : r.status === "undone"
                          ? "Già annullato"
                          : "Oltre la finestra di 7 giorni"
                    }
                  >
                    {busyId === r.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Undo2 className="w-4 h-4" />
                    )}
                    <span className="ml-1">Annulla</span>
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}